import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Upload } from "lucide-react";
import { toast } from "sonner";
import PlanningImport from "@/components/orders/PlanningImport";

const SAMPLE = `MPWO\tSTAGE\tFIELDS (key=value tab-separated)
MPWO-26271678\tprinting\tdate=2026-08-15\toperator_name=Ravi Kumar\tshift=A\tno_of_printed_rolls=8\tmake_ready_wastage_b=120\ttotal_counter_c=5880\tremarks=OK
MPWO-26271678\tavt\tdate=2026-08-16\toperator_name=Sunita\tqty_received_from_printing=5880\tqty_inspected=5860\twastage_qty=20
MPWO-26271678\tslitting\tdate=2026-08-16\toperator_name=Mahesh\tqty_received_from_avt=5860\tqty_forward_to_packing=5850\tqty_wastage=10`;

// Stage keys accepted by the backend importer (kept in sync with production_flow.STAGE_MAP)
const STAGE_KEYS = ["stores_issue", "printing", "flatbed", "avt", "slitting", "quality", "packing", "coa", "dispatch"];

export default function BulkStageImport() {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [xlsxBusy, setXlsxBusy] = useState(false);
  const [xlsxResult, setXlsxResult] = useState(null);

  const submitFile = async () => {
    if (!file) {
      toast.warning("Choose an .xlsx file first");
      return;
    }
    setXlsxBusy(true);
    setXlsxResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/jobs/bulk-file-import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setXlsxResult(data);
      toast.success(`${data.imported} stage${data.imported === 1 ? "" : "s"} updated · ${data.unchanged} already up to date`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setXlsxBusy(false);
    }
  };

  const submit = async () => {
    const rows = text.split(/\r?\n/).filter((r) => r.trim());
    if (!rows.length) {
      toast.warning("Paste at least one row first");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/jobs/bulk-stage-import", { rows });
      setResult(data);
      toast.success(`${data.imported} stage${data.imported === 1 ? "" : "s"} updated · ${data.unchanged} already up to date`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="bulk-stage-import-btn"
          variant="outline"
          size="icon"
          title="Bulk import: jobs from the Planning sheet, and production stage data"
          className="rounded-sm border-neutral-300 text-neutral-700 hover:bg-neutral-100"
        >
          <Upload size={16} />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-6xl w-full rounded-sm max-h-[90vh] overflow-x-hidden overflow-y-auto">
        <DialogHeader className="min-w-0">
          <DialogTitle className="font-display">Bulk Import</DialogTitle>
          <p className="text-xs text-neutral-500 mt-1 break-words">
            Jobs from the Planning sheet, and production stage data. Both are safe to import again: rows already
            imported change nothing, and values entered in the ERP since the last import are kept.
          </p>
        </DialogHeader>

        <div className="mt-3 min-w-0 border border-dashed border-orange-300 rounded-sm bg-orange-50 p-3">
          <div className="text-[10px] uppercase tracking-wider text-orange-700 font-semibold mb-2">
            Jobs · Planning sheet
          </div>
          <PlanningImport uploadOnly />
        </div>

        <div className="mt-4 pt-3 border-t border-neutral-200 min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold mb-1">
            Production stage data
          </div>
          <p className="text-xs text-neutral-600 mb-2">
            A stage log workbook whose sheets are named by stage (Printing / AVT / Slitting / Packing / Dispatch) and have
            an MPWO column, or rows pasted below. Each stage row adds to that job's stage form; it never creates a job.
          </p>
          <div className="flex items-center gap-2">
            <input
              type="file"
              accept=".xlsx,.xlsm"
              data-testid="bulk-xlsx-file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="text-xs file:mr-3 file:rounded-sm file:border file:border-neutral-300 file:bg-white file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-neutral-700"
            />
            <Button
              size="sm"
              onClick={submitFile}
              disabled={xlsxBusy || !file}
              data-testid="bulk-xlsx-submit"
              className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00] text-white"
            >
              <Upload size={13} className="mr-1.5" /> {xlsxBusy ? "Uploading…" : "Upload & Auto-Import"}
            </Button>
          </div>
          {xlsxResult && (
            <div data-testid="bulk-xlsx-result" className="mt-3 border border-neutral-200 rounded-sm p-3 text-xs bg-white space-y-1">
              <StageSummary result={xlsxResult} />
              <div className="text-neutral-700">
                <span className="font-semibold">Sheets processed:</span>
                <ul className="list-disc list-inside mt-1">
                  {xlsxResult.sheets_seen.map((s, i) => (
                    <li key={i}>
                      <span className="font-mono-num">{s.sheet}</span>
                      {s.stage
                        ? <> → <span className="font-semibold text-orange-700">{s.stage}</span> ({s.rows_imported} updated, {s.rows_unchanged || 0} already up to date)</>
                        : <span className="text-neutral-400"> — not a stage sheet</span>}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>

        <div className="mt-4 min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold mb-2">
            Or paste stage rows: MPWO, stage key ({STAGE_KEYS.join(", ")}), then field=value pairs, tab-separated
          </div>
        </div>

        <div className="mt-3 min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold mb-1">Sample format</div>
          <div className="max-w-full overflow-x-auto rounded-sm">
            <pre className="bg-neutral-900 text-neutral-100 p-3 text-[11px] leading-4 whitespace-pre">
{SAMPLE}
            </pre>
          </div>
        </div>

        <div className="mt-3 min-w-0">
          <label className="label-caps block mb-1">Paste rows</label>
          <textarea
            data-testid="bulk-stage-textarea"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={10}
            className="w-full max-w-full rounded-sm border border-neutral-300 px-3 py-2 text-xs font-mono-num focus:outline-none focus:ring-1 focus:ring-[#FF5A00]"
            placeholder={`MPWO-...\tprinting\tkey=value\tkey=value`}
          />
        </div>

        <div className="flex items-center justify-end gap-2 mt-3">
          <Button variant="outline" size="sm" onClick={() => setText(SAMPLE)} data-testid="bulk-stage-sample-btn" className="rounded-sm text-xs">
            Load sample
          </Button>
          <Button
            size="sm"
            onClick={submit}
            disabled={busy || !text.trim()}
            data-testid="bulk-stage-submit"
            className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00] text-white"
          >
            <Upload size={13} className="mr-1.5" /> {busy ? "Importing…" : "Import Rows"}
          </Button>
        </div>

        {result && (
          <div data-testid="bulk-stage-result" className="mt-4 border border-neutral-200 rounded-sm p-3 text-xs bg-neutral-50 space-y-1">
            <StageSummary result={result} />
            {result.errors?.length > 0 && (
              <div>
                <span className="font-semibold text-red-700">Errors:</span>
                <ul className="list-disc list-inside mt-1">
                  {result.errors.slice(0, 20).map((e, i) => (
                    <li key={i}>Row {e.row}: {e.reason}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** What a stage import did: updated, already up to date, repeats merged, app values kept, unknown BPRs. */
function StageSummary({ result }) {
  const kept = result.kept_total || 0;
  return (
    <>
      <div>
        <span className="font-semibold text-emerald-700">{result.imported}</span> stage{result.imported === 1 ? "" : "s"} updated
        {" · "}<span className="font-semibold">{result.unchanged || 0}</span> already up to date
        {result.repeated_rows > 0 && <> · {result.repeated_rows} repeated row{result.repeated_rows === 1 ? "" : "s"} merged</>}
      </div>
      {kept > 0 && (
        <div data-testid="bulk-stage-kept">
          <span className="font-semibold text-amber-700">{kept} value{kept === 1 ? "" : "s"} kept</span>: changed in the app since the
          last import, so the sheet didn't overwrite them
          {result.kept?.length > 0 && (
            <span className="text-neutral-500"> (e.g. {result.kept.slice(0, 3).map((k) => `${k.bpr} ${k.stage}.${k.field}`).join(", ")})</span>
          )}
        </div>
      )}
      {result.unmatched_mpwos?.length > 0 && (
        <div>
          <span className="font-semibold text-amber-700">Not in the ERP (skipped):</span>{" "}
          <span className="font-mono-num">{result.unmatched_mpwos.slice(0, 25).join(", ")}</span>
          {result.unmatched_mpwos.length > 25 && <span className="text-neutral-500"> …+{result.unmatched_mpwos.length - 25} more</span>}
        </div>
      )}
    </>
  );
}
